import { DateTime } from 'luxon';
import type { PlayoutReportEventTypeEvents__Event } from '../queries/PlayoutReportEventTypes.generated';

export const formatMinutes = (ms: number) => Math.round(ms / 6_000) / 10;

export const avgPerEventPerDevice = (
  total: number,
  eventCount: number,
  devices: number | null,
): number | null =>
  eventCount > 0 && devices && devices > 0 ? total / (eventCount * devices) : null;

// Returns the set of ISO date strings for the day-of-start for each event.
// Used by the date-range picker's day marker.
export const toDayKeySet = (
  events: ReadonlyArray<Pick<PlayoutReportEventTypeEvents__Event, 'start'>>,
) =>
  new Set(
    events.map((e) => DateTime.fromISO(e.start).toISODate()).filter((d): d is string => d != null),
  );

// Maps each event-day ISO date to the names of the events starting that day.
export const toEventNamesByDay = (
  events: ReadonlyArray<Pick<PlayoutReportEventTypeEvents__Event, 'start' | 'name'>>,
) => {
  const names = new Map<string, string[]>();
  events.forEach((e) => {
    const key = DateTime.fromISO(e.start).toISODate();
    if (key == null) return;
    const dayNames = names.get(key) ?? [];
    dayNames.push(e.name);
    names.set(key, dayNames);
  });
  return names;
};

const compactNumber = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export const formatCompact = (value: number | null) =>
  value == null ? '' : compactNumber.format(value);

export const EMPTY_STATS = {
  totalPlays: 0,
  totalDurationMs: 0,
  uniqueAssets: 0,
  eventCount: 0,
  uniqueDeviceGroups: 0,
  uniqueDevices: 0,
} as const;

export const EMPTY_REPORT = {
  stats: EMPTY_STATS,
  bySponsorPlays: [],
  bySponsorDuration: [],
  byAssetPlays: [],
  byAssetDuration: [],
  byEvent: [],
  byZone: [],
  byDeviceGroup: [],
  topAssets: [],
} as const;

// Bar chart axis labels: append the sponsor when one is present so two
// distinct sponsors with the same asset name show as separate bars instead
// of colliding on a single label.
export const assetChartLabel = (asset: string, sponsor: string | null | undefined) =>
  sponsor ? `${asset} — ${sponsor}` : asset;

export const zoneDisplayLabel = (zone: string): string => {
  switch (zone) {
    case 'fsBanner':
      return 'FC TV Full Screen Banner';
    case 'fsBumper':
      return 'FC TV Full Screen Bumper';
    case 'fsCommercial':
      return 'FC TV Full Screen Commercial';
    case 'panelBumper':
      return 'FC TV Panel Bumper';
    case 'panelBanner':
      return 'FC TV Panel Banner';
    default:
      return zone;
  }
};
